import { useTheme } from '../../../contexts/ThemeContext';

export default function HowItWorks() {
  const { isDark } = useTheme();

  const steps = [
    {
      number: '01',
      title: 'Assessment',
      description: 'We analyze your workflows and identify where automation delivers the most value.',
      icon: 'ri-search-line',
    },
    {
      number: '02',
      title: 'System Design',
      description: 'Our team designs an AI system tailored to your specific business operations.',
      icon: 'ri-draft-line',
    },
    {
      number: '03',
      title: 'Deployment',
      description: 'We implement automation, integrations, and AI assistants across your systems.',
      icon: 'ri-rocket-line',
    },
    {
      number: '04',
      title: 'Optimization',
      description: 'We monitor performance and continuously refine automation to maximize efficiency.',
      icon: 'ri-line-chart-line',
    },
  ];

  return (
    <section
      id="how-it-works"
      className="py-32 px-8 lg:px-12 transition-colors duration-300"
      style={{ background: isDark ? '#0f1117' : '#ffffff' }}
    >
      <div className="max-w-7xl mx-auto">
        <p
          className="text-xs font-semibold tracking-[0.25em] uppercase text-[#2563EB] mb-5 text-center"
          style={{ fontFamily: 'Montserrat, sans-serif' }}
        >
          The Process
        </p>

        <h2
          className="text-4xl lg:text-5xl font-bold text-center leading-tight tracking-tight mb-6"
          style={{ fontFamily: 'Montserrat, sans-serif', color: isDark ? '#F9FAFB' : '#111827' }}
        >
          Our AI Deployment Framework
        </h2>

        <p
          className="text-lg text-center max-w-2xl mx-auto mb-24 leading-relaxed"
          style={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 400,
            color: isDark ? '#9CA3AF' : '#374151',
          }}
        >
          A structured, four-phase approach that takes your business from assessment to a fully optimized AI-powered operation.
        </p>

        <div className="relative">
          <div
            className="hidden lg:block absolute top-10 left-[12.5%] right-[12.5%] h-px z-0"
            style={{ background: isDark ? 'rgba(255,255,255,0.1)' : '#E5E7EB' }}
          ></div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 relative z-10">
            {steps.map((step, index) => (
              <div key={index} className="group flex flex-col items-center text-center cursor-pointer">
                <div
                  className="w-20 h-20 flex items-center justify-center rounded-full border-2 group-hover:border-[#2563EB] group-hover:bg-[#2563EB] transition-all duration-300 mb-8 relative"
                  style={{
                    background: isDark ? '#1a1f2e' : '#F3F4F6',
                    borderColor: isDark ? 'rgba(255,255,255,0.15)' : '#E5E7EB',
                  }}
                >
                  <i
                    className={`${step.icon} text-2xl group-hover:text-white transition-colors duration-300`}
                    style={{ color: isDark ? '#9CA3AF' : '#374151' }}
                  ></i>
                  <span
                    className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center text-white text-xs font-bold rounded-full"
                    style={{ fontFamily: 'Montserrat, sans-serif', background: '#111827' }}
                  >
                    {index + 1}
                  </span>
                </div>

                <h3
                  className="text-lg font-bold mb-3 tracking-tight"
                  style={{ fontFamily: 'Montserrat, sans-serif', color: isDark ? '#F9FAFB' : '#111827' }}
                >
                  {step.title}
                </h3>
                <p
                  className="text-sm leading-relaxed"
                  style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontWeight: 400,
                    color: isDark ? '#9CA3AF' : '#374151',
                  }}
                >
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-20 text-center">
          <a
            href="#contact"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#2563EB] hover:gap-3 transition-all duration-200 cursor-pointer"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Start your assessment today
            <i className="ri-arrow-right-line text-base"></i>
          </a>
        </div>
      </div>
    </section>
  );
}
